import { createFileRoute } from '@tanstack/react-router';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';
import {
  AcademicLandingPage,
  academicSeo,
} from '@/components/astro/pages/academic-landing-page';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import { seo } from '@/seo/seo';

const ANALYTICS = experimentalRoutes['/_marketing/_seo/academic'];

/**
 * `/academic` — the organic/SEO academic landing URL, and the primary
 * destination for academic ad spend.
 *
 * This route existed from `1236ab355` until `31caf4428` (2025-07-09) deleted it
 * as a duplicate of `/fb/academic`, in a commit titled "Small styling change |
 * New images" that also dropped its footer link. Since then `/academic` fell
 * through to the `$keywordToolSlug` catch-all, which `notFound()`s because
 * `academic` is not a keyword-tool slug — so a paid-ad destination served a
 * 404, and `redirects.ts` has pointed `/academic-old` at it the whole time.
 * Restoring it fixes that redirect and gives the `academic-landing-funnel-v1`
 * experiment a URL that ads can point at.
 *
 * This route is the canonical of the pair — `/fb/academic` points its canonical
 * here, so search engines consolidate the duplicate into this URL.
 *
 * `cdn-shared`: the HTML is cached at the CDN with the variant in the cache
 * key, so nothing per-user may render server-side. `_marketing.tsx`'s loader
 * skips its `User.Summary` lookup for exactly this reason. The canonical is the
 * same for both arms, so it is cache-safe.
 */
export const Route = createFileRoute('/_marketing/_seo/academic')({
  staticData: { analytics: ANALYTICS },
  headers: cacheHeadersFor(ANALYTICS),
  head: () => ({
    meta: [...seo(academicSeo)],
    links: [{ rel: 'canonical', href: normalizeCanonicalUrl('/academic') }],
  }),
  component: AcademicLandingPage,
});
